'use client';

export interface UserProfile {
  id: string;
  email: string;
  role?: string;
  created_at?: string;
}

export interface AuthSession {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  user: UserProfile;
}

const STORAGE_KEY = 'picel_auth_session';

export function getSupabaseConfig() {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL || 'https://yydzxjuaxsjzxqwopmjp.supabase.co';
  const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY || '';
  return { url: url.replace(/\/$/, ''), anonKey };
}

type AuthCallback = (session: AuthSession | null) => void;
const listeners: Set<AuthCallback> = new Set();

function emit(session: AuthSession | null) {
  for (const fn of listeners) {
    try { fn(session); } catch (e) { console.error(e); }
  }
}

export function handleOAuthCallback(): AuthSession | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash;
  if (!hash || !hash.includes('access_token=')) return null;

  try {
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const expiresIn = Number(params.get('expires_in') || 3600);

    if (accessToken) {
      let user: UserProfile = { id: '', email: '' };
      try {
        const payloadBase64 = accessToken.split('.')[1];
        if (payloadBase64) {
          const decoded = JSON.parse(atob(payloadBase64));
          user = {
            id: decoded.sub || '',
            email: decoded.email || '',
            role: decoded.role,
          };
        }
      } catch {
        user = { id: 'oauth_user', email: params.get('email') || '' };
      }

      const session: AuthSession = {
        access_token: accessToken,
        refresh_token: refreshToken || '',
        expires_at: Date.now() + expiresIn * 1000,
        user,
      };
      setStoredSession(session);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      return session;
    }
  } catch (err) {
    console.error('Failed to parse OAuth callback:', err);
  }
  return null;
}

let isRefreshing = false;

export function getStoredSession(): AuthSession | null {
  if (typeof window === 'undefined') return null;
  const oauth = handleOAuthCallback();
  if (oauth) return oauth;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.access_token && parsed.user) {
      if (parsed.expires_at && parsed.expires_at < Date.now()) {
        if (parsed.refresh_token && !isRefreshing) {
          isRefreshing = true;
          void refreshSession().finally(() => { isRefreshing = false; });
        } else if (!parsed.refresh_token) {
          setStoredSession(null);
          return null;
        }
      }
      return parsed;
    }
  } catch {}
  return null;
}

export function setStoredSession(session: AuthSession | null) {
  if (typeof window === 'undefined') return;
  if (session) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
  emit(session);
}

export async function refreshSession(): Promise<AuthSession | null> {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  let session: AuthSession | null = null;
  try { session = JSON.parse(raw); } catch { return null; }
  if (!session?.refresh_token) {
    setStoredSession(null);
    return null;
  }
  const { url, anonKey } = getSupabaseConfig();
  try {
    const res = await fetch(`${url}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: anonKey,
      },
      body: JSON.stringify({ refresh_token: session.refresh_token }),
    });
    const data = await res.json();
    if (!res.ok || !data.access_token) {
      setStoredSession(null);
      return null;
    }
    const nextSession: AuthSession = {
      access_token: data.access_token,
      refresh_token: data.refresh_token || session.refresh_token,
      expires_at: Date.now() + (data.expires_in || 3600) * 1000,
      user: data.user ? { id: data.user.id, email: data.user.email, role: data.user.role } : session.user,
    };
    setStoredSession(nextSession);
    return nextSession;
  } catch {
    setStoredSession(null);
    return null;
  }
}

export function getGoogleOAuthUrl(redirectTo?: string): string {
  const { url } = getSupabaseConfig();
  const callbackUrl = redirectTo || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${url}/auth/v1/authorize?provider=google&redirect_to=${encodeURIComponent(callbackUrl)}`;
}

export function signInWithGoogle(redirectTo?: string) {
  if (typeof window === 'undefined') return;
  const targetUrl = getGoogleOAuthUrl(redirectTo);
  window.location.href = targetUrl;
}

export async function signUp(email: string, password: string): Promise<{ user?: UserProfile; error?: string }> {
  const { url, anonKey } = getSupabaseConfig();
  try {
    const res = await fetch(`${url}/auth/v1/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: anonKey,
      },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.msg || data.error_description || data.message || 'Sign up failed' };
    if (data.access_token && data.user) {
      const session: AuthSession = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_at: Date.now() + (data.expires_in || 3600) * 1000,
        user: { id: data.user.id, email: data.user.email, role: data.user.role },
      };
      setStoredSession(session);
    }
    return { user: data.user ? { id: data.user.id, email: data.user.email } : undefined };
  } catch (err: any) {
    return { error: err.message || 'Network error during sign up' };
  }
}

export async function signIn(email: string, password: string): Promise<{ session?: AuthSession; error?: string }> {
  const { url, anonKey } = getSupabaseConfig();
  try {
    const res = await fetch(`${url}/auth/v1/token?grant_type=password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: anonKey,
      },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error_description || data.msg || data.message || 'Sign in failed' };
    const session: AuthSession = {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_at: Date.now() + (data.expires_in || 3600) * 1000,
      user: { id: data.user.id, email: data.user.email, role: data.user.role },
    };
    setStoredSession(session);
    return { session };
  } catch (err: any) {
    return { error: err.message || 'Network error during sign in' };
  }
}

export async function signOut(): Promise<void> {
  const session = getStoredSession();
  const { url, anonKey } = getSupabaseConfig();
  if (session) {
    try {
      await fetch(`${url}/auth/v1/logout`, {
        method: 'POST',
        headers: {
          apikey: anonKey,
          Authorization: `Bearer ${session.access_token}`,
        },
      });
    } catch {}
  }
  setStoredSession(null);
}

export function onAuthStateChange(callback: AuthCallback): () => void {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}
